import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { takePendingFile } from "@/lib/pending-upload";
import { motion, AnimatePresence } from "framer-motion";
import { Shell } from "@/components/layout/Shell";
import { useSeo } from "@/hooks/use-seo";
import { AdSlot } from "@/components/ads/AdSlot";
import { ShareButtons } from "@/components/social/ShareButtons";
import { extractDominantColors, classifyAuraFamily, AURA_READS, AuraColor } from "@/lib/aura-extract";
import { Download, ChevronDown, Image as ImageIcon, LayoutTemplate, Braces, Palette } from "lucide-react";
import { cn } from "@/lib/utils";

// ── Export helpers ────────────────────────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Draw text wrapped to maxWidth, returning the y position after the last line. */
function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): number {
  const words = text.split(" ");
  let line = "";
  let curY = y;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, curY);
      curY += lineHeight;
      line = word;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, curY);
  return curY + lineHeight;
}

/** Draw the aura card onto a canvas of given W×H and trigger a PNG download. */
async function exportAuraCard(
  imageUrl: string,
  colors: AuraColor[],
  title: string,
  description: string,
  W: number,
  H: number,
  filename: string,
): Promise<void> {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  // ── Background gradient ────────────────────────────────────────────────────
  const bg = ctx.createRadialGradient(W * 0.28, H * 0.2, 0, W * 0.5, H * 0.5, Math.max(W, H) * 0.85);
  bg.addColorStop(0, `${colors[0]?.hex ?? "#6b21a8"}cc`);
  bg.addColorStop(0.5, `${colors[1]?.hex ?? colors[0]?.hex ?? "#312e81"}88`);
  bg.addColorStop(1, "#09090b");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  const bg2 = ctx.createRadialGradient(W * 0.78, H * 0.72, 0, W * 0.75, H * 0.72, W * 0.55);
  bg2.addColorStop(0, `${colors[2]?.hex ?? colors[0]?.hex ?? "#1e1b4b"}66`);
  bg2.addColorStop(1, "transparent");
  ctx.fillStyle = bg2;
  ctx.fillRect(0, 0, W, H);

  const vignette = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.25, W / 2, H / 2, Math.max(W, H) * 0.75);
  vignette.addColorStop(0, "transparent");
  vignette.addColorStop(1, "rgba(0,0,0,0.55)");
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, W, H);

  // ── Load photo ─────────────────────────────────────────────────────────────
  const photoImg = new Image();
  photoImg.crossOrigin = "anonymous";
  await new Promise<void>((resolve) => {
    photoImg.onload = () => resolve();
    photoImg.onerror = () => resolve();
    photoImg.src = imageUrl;
  });

  // Photo radius scales with width
  const photoR = Math.round(W * 0.14);
  const photoCX = W / 2;
  const photoTopPad = H > W ? H * 0.08 : H * 0.09; // story vs square
  const photoCY = photoTopPad + photoR;

  // Glow ring
  ctx.save();
  ctx.shadowColor = colors[0]?.hex ?? "#a855f7";
  ctx.shadowBlur = 50;
  ctx.beginPath();
  ctx.arc(photoCX, photoCY, photoR + 4, 0, Math.PI * 2);
  ctx.strokeStyle = `${colors[0]?.hex ?? "#a855f7"}88`;
  ctx.lineWidth = 4;
  ctx.stroke();
  ctx.restore();

  // Circular clip + draw
  ctx.save();
  ctx.beginPath();
  ctx.arc(photoCX, photoCY, photoR, 0, Math.PI * 2);
  ctx.clip();
  if (photoImg.width > 0) {
    const d = photoR * 2;
    const scale = Math.max(d / photoImg.width, d / photoImg.height);
    const sw = d / scale, sh = d / scale;
    const sx = (photoImg.width - sw) / 2, sy = (photoImg.height - sh) / 2;
    ctx.drawImage(photoImg, sx, sy, sw, sh, photoCX - photoR, photoCY - photoR, d, d);
  } else {
    ctx.fillStyle = colors[0]?.hex ?? "#a855f7";
    ctx.arc(photoCX, photoCY, photoR, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // ── Label + title ──────────────────────────────────────────────────────────
  const labelY = photoCY + photoR + Math.round(H * 0.05);
  ctx.textAlign = "center";
  ctx.font = `500 ${Math.round(W * 0.024)}px system-ui, sans-serif`;
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.fillText("YOUR AURA SIGNATURE", W / 2, labelY);

  const titleSize = Math.round(W * 0.065);
  ctx.font = `bold ${titleSize}px Georgia, serif`;
  ctx.fillStyle = "#ffffff";
  ctx.shadowColor = `${colors[0]?.hex ?? "#a855f7"}bb`;
  ctx.shadowBlur = 24;
  ctx.fillText(title, W / 2, labelY + Math.round(H * 0.075));
  ctx.shadowBlur = 0;

  // ── Swatches ───────────────────────────────────────────────────────────────
  const swatchR = Math.round(W * 0.034);
  const swatchGap = Math.round(W * 0.115);
  const swatchCY = labelY + Math.round(H * 0.075) + Math.round(H * 0.075);
  const roleLabels = ["Dominant", "Secondary", "Accent"];

  colors.slice(0, 3).forEach((c, i) => {
    const cx = W / 2 + (i - 1) * swatchGap;
    const cy = swatchCY;

    ctx.save();
    ctx.shadowColor = c.hex;
    ctx.shadowBlur = 16;
    ctx.beginPath();
    ctx.arc(cx, cy, swatchR, 0, Math.PI * 2);
    ctx.fillStyle = c.hex;
    ctx.fill();
    ctx.restore();

    ctx.beginPath();
    ctx.arc(cx, cy, swatchR + 3, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(255,255,255,0.2)";
    ctx.lineWidth = 2;
    ctx.stroke();

    const hexSize = Math.round(W * 0.021);
    ctx.font = `600 ${hexSize}px monospace`;
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.fillText(c.hex.toUpperCase(), cx, cy + swatchR + Math.round(H * 0.028));

    ctx.font = `400 ${Math.round(W * 0.017)}px system-ui, sans-serif`;
    ctx.fillStyle = "rgba(255,255,255,0.45)";
    ctx.fillText(`${roleLabels[i]} · ${Math.round(c.weight * 100)}%`, cx, cy + swatchR + Math.round(H * 0.052));
  });

  // ── Description ────────────────────────────────────────────────────────────
  const maxDescChars = H > W ? 320 : 220;
  const truncated = description.length > maxDescChars
    ? description.slice(0, maxDescChars - 1) + "…"
    : description;
  const descFontSize = Math.round(W * 0.026);
  const descLineH = Math.round(descFontSize * 1.55);
  ctx.font = `300 ${descFontSize}px system-ui, sans-serif`;
  ctx.fillStyle = "rgba(255,255,255,0.78)";
  ctx.textAlign = "center";
  const descTopY = swatchCY + swatchR + Math.round(H * 0.095);
  drawWrappedText(ctx, truncated, W / 2, descTopY, W - Math.round(W * 0.18), descLineH);

  // ── Branding ───────────────────────────────────────────────────────────────
  ctx.font = `500 ${Math.round(W * 0.022)}px system-ui, sans-serif`;
  ctx.fillStyle = "rgba(255,255,255,0.3)";
  ctx.textAlign = "center";
  ctx.fillText("mychromamind.com", W / 2, H - Math.round(H * 0.038));

  // ── Download ───────────────────────────────────────────────────────────────
  canvas.toBlob((blob) => {
    if (blob) downloadBlob(blob, filename);
  }, "image/png");
}

/** Simple horizontal color strip — 3 swatches with hex + weight. */
function exportColorStrip(colors: AuraColor[], title: string): void {
  const W = 900, ROW = 80, PAD = 28, HEADER = 72;
  const H = HEADER + colors.slice(0, 3).length * ROW + PAD;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = "#0c0c10";
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = "#ffffff";
  ctx.font = "600 22px Georgia, serif";
  ctx.textAlign = "left";
  ctx.fillText(`Aura: ${title}`, PAD, 36);
  ctx.font = "13px system-ui, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.4)";
  ctx.fillText("mychromamind.com/aura-scanner", PAD, 58);

  const roleLabels = ["Dominant", "Secondary", "Accent"];
  colors.slice(0, 3).forEach((c, i) => {
    const y = HEADER + i * ROW;
    const swatchSize = 48;
    const sy = y + (ROW - swatchSize) / 2;

    ctx.save();
    ctx.shadowColor = c.hex;
    ctx.shadowBlur = 10;
    ctx.fillStyle = c.hex;
    ctx.beginPath();
    ctx.roundRect(PAD, sy, swatchSize, swatchSize, 10);
    ctx.fill();
    ctx.restore();

    ctx.fillStyle = "#ffffff";
    ctx.font = "600 16px system-ui, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(roleLabels[i] ?? "", PAD + swatchSize + 18, y + ROW / 2 - 5);

    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "13px monospace";
    ctx.fillText(c.hex.toUpperCase(), PAD + swatchSize + 18, y + ROW / 2 + 16);

    ctx.fillStyle = "#ffffff";
    ctx.font = "600 15px system-ui, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(`${Math.round(c.weight * 100)}%`, W - PAD, y + ROW / 2 + 5);
  });

  canvas.toBlob((blob) => {
    if (blob) downloadBlob(blob, "aura-color-strip.png");
  }, "image/png");
}

/** Raw JSON export of the aura result. */
function exportJson(colors: AuraColor[], title: string, family: string, description: string): void {
  const data = {
    auraTitle: title,
    family,
    description,
    colors: colors.slice(0, 3).map((c, i) => ({
      role: ["dominant", "secondary", "accent"][i],
      hex: c.hex,
      coveragePercent: Math.round(c.weight * 100),
    })),
    source: "mychromamind.com/aura-scanner",
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  downloadBlob(blob, "aura-result.json");
}

type Status = "idle" | "scanning" | "success" | "error";

const MAX_FILE_BYTES = 8 * 1024 * 1024; // 8MB

export default function AuraScanner() {
  useSeo({
    title: "Aura Photo Scanner — Extract Your Dominant Colors | Mychromamind",
    description:
      "Upload a photo and let the Aura Scanner extract your top 3 dominant colors on an HTML5 canvas, then read the color-psychology personality profile behind your aura — entirely in your browser.",
    path: "/aura-scanner",
    ogType: "website",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebApplication",
          name: "Aura Photo Scanner",
          applicationCategory: "LifestyleApplication",
          operatingSystem: "Any (Web)",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          description:
            "Upload a photo to extract its top 3 dominant colors and get a color-psychology personality read based on your aura's dominant hue.",
          url: "https://mychromamind.com/aura-scanner",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Mychromamind", item: "https://mychromamind.com/" },
            { "@type": "ListItem", position: 2, name: "Aura Scanner", item: "https://mychromamind.com/aura-scanner" },
          ],
        },
      ],
    },
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [colors, setColors] = useState<AuraColor[]>([]);
  const [error, setError] = useState("");

  const runScan = useCallback((url: string) => {
    setStatus("scanning");
    setError("");
    const img = new Image();
    img.onload = () => {
      // Small artificial delay so the scanning animation reads as a real
      // analysis pass rather than an instant flash — the extraction itself
      // is effectively instant.
      window.setTimeout(() => {
        try {
          const dominant = extractDominantColors(img, 3);
          if (dominant.length === 0) {
            setError("Couldn't read any color data from that image. Try a different photo.");
            setStatus("error");
            return;
          }
          setColors(dominant);
          setStatus("success");
        } catch {
          setError("Something went wrong while scanning that image. Try a different photo.");
          setStatus("error");
        }
      }, 900);
    };
    img.onerror = () => {
      setError("That file couldn't be loaded as an image. Try a JPG or PNG.");
      setStatus("error");
    };
    img.src = url;
  }, []);

  const handleFile = useCallback((file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file (JPG, PNG, WEBP).");
      setStatus("error");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError("That image is too large — please choose one under 8MB.");
      setStatus("error");
      return;
    }
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    runScan(url);
  }, [runScan]);

  // Auto-load a file passed from the homepage via the pending-upload store.
  useEffect(() => {
    const pending = takePendingFile();
    if (pending) handleFile(pending);
  }, [handleFile]);

  const handleReset = () => {
    setStatus("idle");
    setImageUrl(null);
    setColors([]);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const dominant = colors[0];
  const family = dominant ? classifyAuraFamily(dominant.hex) : null;
  const read = family ? AURA_READS[family] : null;

  const [exportOpen, setExportOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!exportOpen) return;
    const handler = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [exportOpen]);

  const EXPORT_OPTIONS = [
    {
      id: "square",
      label: "Aura Card — Square",
      hint: "1080×1080 · Instagram / social post",
      icon: ImageIcon,
      run: () => read && imageUrl && exportAuraCard(imageUrl, colors, read.title, read.description, 1080, 1080, "my-aura-square.png"),
    },
    {
      id: "story",
      label: "Aura Card — Story",
      hint: "1080×1920 · Instagram / TikTok story",
      icon: LayoutTemplate,
      run: () => read && imageUrl && exportAuraCard(imageUrl, colors, read.title, read.description, 1080, 1920, "my-aura-story.png"),
    },
    {
      id: "strip",
      label: "Color Strip",
      hint: "Palette PNG — for moodboards & design files",
      icon: Palette,
      run: () => read && exportColorStrip(colors, read.title),
    },
    {
      id: "json",
      label: "JSON",
      hint: "Raw data — colors, title, description",
      icon: Braces,
      run: () => read && family && exportJson(colors, read.title, family, read.description),
    },
  ] as const;

  const glowStyle = dominant
    ? {
        background: [
          `radial-gradient(circle at 28% 25%, ${colors[0]?.hex ?? "#8884"}88, transparent 55%)`,
          `radial-gradient(circle at 75% 70%, ${colors[1]?.hex ?? colors[0]?.hex ?? "#8884"}77, transparent 55%)`,
          `radial-gradient(circle at 50% 95%, ${colors[2]?.hex ?? colors[0]?.hex ?? "#8884"}55, transparent 60%)`,
        ].join(", "),
      }
    : undefined;

  const writeupWordCount = read?.description ?? "";

  return (
    <Shell>
      <div className="max-w-4xl mx-auto px-5 sm:px-6 py-10 md:py-16 w-full">
        <h1 className="text-3xl sm:text-5xl font-serif mb-4 leading-tight text-center">
          Aura Scanner
        </h1>
        <p className="text-muted-foreground text-base sm:text-lg text-center max-w-2xl mx-auto mb-10">
          Upload a photo — a selfie, a favorite place, anything — and the scanner will read its
          three most dominant colors, then reveal the personality signature behind your aura.
          Everything happens right in your browser; the photo is never uploaded anywhere.
        </p>

        {/* Upload zone */}
        {status !== "success" && (
          <div className="max-w-lg mx-auto">
            <label
              htmlFor="aura-file-input"
              className="group flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-border hover:border-primary/50 bg-card/60 hover:bg-card transition-colors cursor-pointer px-6 py-14 text-center"
            >
              <span className="text-4xl" aria-hidden="true">📸</span>
              <span className="font-medium text-foreground">
                {status === "scanning" ? "Scanning your photo…" : "Click to upload a photo"}
              </span>
              <span className="text-xs text-muted-foreground">JPG, PNG, or WEBP — up to 8MB</span>
              <input
                id="aura-file-input"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                disabled={status === "scanning"}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>

            <AnimatePresence>
              {status === "scanning" && imageUrl && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="relative mt-6 rounded-2xl overflow-hidden border border-border"
                >
                  {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                  <img ref={imgRef} src={imageUrl} alt="Uploaded photo being scanned" className="w-full h-64 object-cover" />
                  <motion.div
                    className="absolute inset-x-0 h-1/3 bg-gradient-to-b from-white/40 to-transparent"
                    initial={{ top: "-33%" }}
                    animate={{ top: ["-33%", "100%"] }}
                    transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
                  />
                  <div className="absolute bottom-3 left-0 right-0 text-center text-xs font-medium text-white drop-shadow">
                    Scanning pixels on canvas…
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {status === "error" && (
              <div role="alert" className="mt-4 p-4 rounded-xl border border-destructive/30 bg-destructive/5 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Result */}
        {status === "success" && dominant && read && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div
              className="relative rounded-3xl overflow-hidden border border-border/60 p-6 sm:p-10 mb-8"
              style={glowStyle}
            >
              <div className="relative flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt="Your uploaded photo"
                    className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover border-4 border-background shadow-xl shrink-0"
                  />
                )}
                <div className="flex-1 text-center sm:text-left">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">
                    Your Aura Signature
                  </p>
                  <h2 className="text-2xl sm:text-4xl font-serif mb-4">{read.title}</h2>
                  <div className="flex items-center justify-center sm:justify-start gap-2">
                    {colors.map((c) => (
                      <span
                        key={c.hex}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full ring-2 ring-background shadow-md"
                        style={{ backgroundColor: c.hex }}
                        title={`${c.hex} · ${Math.round(c.weight * 100)}%`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mb-8">
              {colors.map((c, i) => (
                <div key={c.hex} className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full shrink-0" style={{ backgroundColor: c.hex }} />
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-medium">{c.hex}</p>
                    <p className="text-xs text-muted-foreground">
                      {i === 0 ? "Dominant" : i === 1 ? "Secondary" : "Accent"} · {Math.round(c.weight * 100)}% of image
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="max-w-2xl mx-auto text-center mb-10">
              <p className="text-muted-foreground leading-relaxed">{writeupWordCount}</p>
            </div>

            <AdSlot slotId="2431849490" content={[read.description]} className="my-8 max-w-2xl mx-auto" />

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              {/* Export dropdown */}
              <div className="relative" ref={exportMenuRef}>
                <button
                  type="button"
                  onClick={() => setExportOpen((v) => !v)}
                  aria-expanded={exportOpen}
                  aria-haspopup="menu"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm"
                >
                  <Download className="w-4 h-4" />
                  Export
                  <ChevronDown className={cn("w-4 h-4 transition-transform", exportOpen && "rotate-180")} />
                </button>

                <AnimatePresence>
                  {exportOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.15 }}
                      role="menu"
                      className="absolute z-10 bottom-full mb-2 left-1/2 -translate-x-1/2 w-72 rounded-2xl border border-border bg-popover shadow-xl overflow-hidden"
                    >
                      {EXPORT_OPTIONS.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          role="menuitem"
                          onClick={() => { opt.run(); setExportOpen(false); }}
                          className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted transition-colors"
                        >
                          <opt.icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                          <span>
                            <span className="block text-sm font-medium">{opt.label}</span>
                            <span className="block text-xs text-muted-foreground">{opt.hint}</span>
                          </span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <button
                type="button"
                onClick={handleReset}
                className="px-6 py-3 rounded-full border border-border hover:bg-muted transition-colors font-medium text-sm"
              >
                Scan Another Photo
              </button>
              <Link href="/quiz/personality" className="px-6 py-3 rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors font-medium text-sm">
                Take the Personality Quiz
              </Link>
            </div>

            <ShareButtons
              label="Share your Aura"
              text={`My aura reads as "${read.title}" on Mychromamind's Aura Scanner. Try it on your own photo:`}
              className="mb-4"
            />

            <p className="text-center text-xs text-muted-foreground max-w-md mx-auto">
              For fun and reflection only — not psychological advice. Your photo is processed
              entirely in your browser and never uploaded to a server.
            </p>
          </motion.div>
        )}
      </div>
    </Shell>
  );
}
